<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-title>
              <h3 class="font-weight-bold">
                {{ isEditMode ? 'Edit Chat Message' : 'Chat Message Details' }}
              </h3>
              <v-spacer></v-spacer>
              <!-- Edit/Read-Only Toggle Button -->
              <v-btn
                :color="isEditMode ? 'warning' : 'primary'"
                :outlined="!isEditMode"
                @click="toggleEditMode"
                class="mr-2"
                small
              >
                <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
              </v-btn>
              
              <v-btn
                color="grey darken-1"
                outlined
                @click="$router.back()"
                class="back-button"
                small
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back to Messages
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-progress-linear
                v-if="isLoading"
                indeterminate
                color="primary"
                class="mb-4"
              ></v-progress-linear>

              <v-form
                v-else
                ref="form"
                v-model="valid"
                lazy-validation
              >
                <v-row>
                  <!-- Chat Information (Read-only) -->
                  <v-col md="6">
                    <v-text-field
                      :value="data.chat ? (data.chat.name || 'Direct Chat') : ''"
                      label="Chat"
                      prepend-inner-icon="mdi-message"
                      readonly
                      outlined
                    >
                      <template v-slot:append>
                        <v-chip
                          small
                          :color="data.chat && data.chat.is_group ? 'blue' : 'green'"
                          text-color="white"
                        >
                          {{ data.chat && data.chat.is_group ? 'Group Chat' : 'Direct Chat' }}
                        </v-chip>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Sender Information (Read-only) -->
                  <v-col md="6">
                    <v-text-field
                      :value="data.sender ? (data.sender.user_name || data.sender.email) : ''"
                      label="Sender"
                      prepend-inner-icon="mdi-account"
                      readonly
                      outlined
                    >
                      <template v-slot:append>
                        <v-avatar size="32">
                          <v-img
                            v-if="data.sender && data.sender.image_path"
                            :src="`${mediaUrl}${data.sender.image_path}`"
                            :alt="data.sender.user_name"
                          ></v-img>
                          <v-icon v-else>mdi-account-circle</v-icon>
                        </v-avatar>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <!-- Message Content -->
                  <v-col md="12">
                    <v-textarea
                      v-model="form.message"
                      label="Message Content"
                      :rules="messageRules"
                      :error-messages="getError('message')"
                      prepend-inner-icon="mdi-message-text"
                      outlined
                      rows="4"
                      counter="1000"
                      :readonly="!isEditMode"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <v-row v-if="isEditMode">
                  <!-- File Upload (only in edit mode) -->
                  <v-col md="6">
                    <v-file-input
                      v-model="form.file"
                      label="Update Attached File"
                      :rules="fileRules"
                      :error-messages="getError('file')"
                      prepend-icon="mdi-paperclip"
                      outlined
                      show-size
                      accept="*/*"
                      hint="Maximum file size: 10MB. Leave empty to keep current file."
                    ></v-file-input>
                  </v-col>

                  <!-- Active Status (only in edit mode) -->
                  <v-col md="6">
                    <v-switch
                      v-model="form.is_active"
                      :label="`Message Status: ${form.is_active ? 'Active' : 'Inactive'}`"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>

                <!-- Current File Display -->
                <v-row v-if="data.file_path">
                  <v-col md="12">
                    <v-card outlined>
                      <v-card-subtitle>
                        <v-icon left>mdi-file</v-icon>
                        Current Attached File
                      </v-card-subtitle>
                      <v-card-text>
                        <v-chip
                          color="blue"
                          dark
                          @click="downloadFile(data.file_path)"
                          class="mr-2"
                        >
                          <v-icon left>mdi-download</v-icon>
                          Download File
                        </v-chip>
                        <small class="text--secondary ml-2">
                          {{ getFileName(data.file_path) }}
                        </small>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Message Status Information -->
                <v-row>
                  <v-col md="3">
                    <v-card outlined class="status-card">
                      <v-card-text class="text-center">
                        <v-icon
                          :color="data.is_seen ? 'green' : 'orange'"
                          size="32"
                        >
                          {{ data.is_seen ? 'mdi-eye' : 'mdi-eye-off' }}
                        </v-icon>
                        <div class="mt-2">
                          <strong>{{ data.is_seen ? 'Seen' : 'Unseen' }}</strong>
                        </div>
                        <small v-if="data.is_seen && data.seen_at">
                          {{ formatDate(data.seen_at) }}
                        </small>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col md="3">
                    <v-card outlined class="status-card">
                      <v-card-text class="text-center">
                        <v-icon
                          :color="data.is_active ? 'green' : 'red'"
                          size="32"
                        >
                          {{ data.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                        <div class="mt-2">
                          <strong>{{ data.is_active ? 'Active' : 'Inactive' }}</strong>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col md="3">
                    <v-card outlined class="status-card">
                      <v-card-text class="text-center">
                        <v-icon color="blue" size="32">mdi-calendar</v-icon>
                        <div class="mt-2">
                          <strong>Created</strong>
                        </div>
                        <small>{{ formatDate(data.created_at) }}</small>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col md="3">
                    <v-card outlined class="status-card">
                      <v-card-text class="text-center">
                        <v-icon color="purple" size="32">mdi-update</v-icon>
                        <div class="mt-2">
                          <strong>Updated</strong>
                        </div>
                        <small>{{ formatDate(data.updated_at) }}</small>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row class="mt-4">
                  <v-col md="12" class="text-right">
                    <v-btn
                      color="grey"
                      outlined
                      @click="handleCancel"
                      class="mr-2"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      {{ isEditMode ? 'Cancel' : 'Back' }}
                    </v-btn>

                    <v-btn
                      v-if="!isEditMode"
                      color="warning"
                      @click="toggleSeenStatus"
                      class="mr-2"
                    >
                      <v-icon left>
                        {{ data.is_seen ? 'mdi-eye-off' : 'mdi-eye' }}
                      </v-icon>
                      Mark as {{ data.is_seen ? 'Unseen' : 'Seen' }}
                    </v-btn>

                    <v-btn
                      v-if="isEditMode"
                      color="primary"
                      @click="submitForm"
                      :loading="isSubmitting"
                      :disabled="!isFormValid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Message
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  data() {
    return {
      valid: false,
      isLoading: true,
      isSubmitting: false,
      isEditMode: false,
      data: {},
      form: {
        message: "",
        file: null,
        is_active: true,
      },
      messageRules: [
        (v) => {
          if (!v && !this.form.file && !this.data.file_path) {
            return "Either message content or file attachment is required";
          }
          if (v && v.length > 1000) {
            return "Message cannot exceed 1000 characters";
          }
          return true;
        },
      ],
      fileRules: [
        (v) => {
          if (v && v.size > 10 * 1024 * 1024) {
            return "File size cannot exceed 10MB";
          }
          return true;
        },
      ],
      errors: {},
    };
  },
  computed: {
    isFormValid() {
      return (
        (this.form.message || this.form.file || this.data.file_path) &&
        this.valid
      );
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const messageId = this.$route.params.id;
        const response = await axios.get(`chat_messages/${messageId}`);
        this.data = response.data.data;
        
        // Initialize form with current data
        this.form = {
          message: this.data.message || "",
          file: null,
          is_active: this.data.is_active,
        };
      } catch (error) {
        console.error("Error fetching chat message:", error);
        this.$store.dispatch("errors/setError", "Failed to fetch chat message details");
        this.$router.push("/chat-messages");
      } finally {
        this.isLoading = false;
      }
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
        // Reset form to original data
        this.form = {
          message: this.data.message || "",
          file: null,
          is_active: this.data.is_active,
        };
        this.errors = {};
      }
    },

    handleCancel() {
      if (this.isEditMode) {
        this.toggleEditMode();
      } else {
        this.$router.push("/chat-messages");
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSubmitting = true;
      this.errors = {};

      try {
        const formData = new FormData();
        
        if (this.form.message !== this.data.message) {
          formData.append("message", this.form.message);
        }
        
        if (this.form.file) {
          formData.append("file", this.form.file);
        }
        
        if (this.form.is_active !== this.data.is_active) {
          formData.append("is_active", this.form.is_active ? 1 : 0);
        }

        const messageId = this.$route.params.id;
        await axios.post(`chat_messages/${messageId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-HTTP-Method-Override": "PUT",
          },
        });

        this.$store.dispatch("errors/setSuccess", "Chat message updated successfully!");
        this.isEditMode = false;
        this.getData(); // Refresh data
      } catch (error) {
        console.error("Error updating chat message:", error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$store.dispatch("errors/setError", 
            error.response?.data?.message || "Failed to update chat message");
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async toggleSeenStatus() {
      try {
        const endpoint = this.data.is_seen 
          ? `chat_messages/${this.$route.params.id}/mark_unseen`
          : `chat_messages/${this.$route.params.id}/mark_seen`;
        
        await axios.post(endpoint);
        this.$store.dispatch("errors/setSuccess", 
          `Message marked as ${this.data.is_seen ? 'unseen' : 'seen'}`);
        this.getData(); // Refresh data
      } catch (error) {
        console.error("Error toggling seen status:", error);
        this.$store.dispatch("errors/setError", "Failed to update seen status");
      }
    },

    downloadFile(filePath) {
      if (filePath) {
        const url = `${this.mediaUrl}/${filePath}`;
        window.open(url, "_blank");
      }
    },

    getFileName(filePath) {
      if (!filePath) return '';
      return filePath.split('/').pop();
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleString();
    },

    getError(field) {
      return this.errors[field] ? this.errors[field][0] : "";
    },
  },
};
</script>

<style scoped>
.status-card {
  height: 120px;
  display: flex;
  align-items: center;
}

.status-card .v-card__text {
  padding: 16px 12px;
}

.file-preview {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}
</style>